import { SM_PageHeader } from "@/_self_managed/comps/SM_PageHeader";
import { useAutoGenFilterConfigsFromTpl } from "@/components/analysisModules/utils/useAutoGenFilterConfigsFromTpl";
import ErrorStates from "@/components/ErrorStates";
import { ListingLayout } from "@/pageTpls/ListingPage/ListingLayout";
import { useDebounce } from "@/utils/customHooks";
import { buildListingPageQueryForKS } from "@/utils/okf-ks-utils/buildListingPageQueryForKS";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetGlobalSearchDataLazy } from "@/utils/react-query-hooks/getData";
import { getRoutePathToPublishedContent } from "@okf/ootils";
import {
  Container,
  getBreakPoint,
  Section,
  useScreenWidth,
  Wrapper1000,
} from "oolib";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const GlobalSearch = () => {
  const [searchTermInputText, setSearchTermInputText] = useState("");
  const [searchTermInputValue, setSearchTermInputValue] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const navigate = useNavigate();
  const debounce = useDebounce();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= getBreakPoint("md");
  const allTpls = useGetQueryData("allTpls");
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");

  const selectedTpls = useMemo(
    () => allTpls.filter((tpl) => tpl.general.segment === "publishing"),
    [allTpls]
  );
  
  const {
    error: contentError,
    data: contentData,
    status: contentStatus,
    fetchNextPage: contentFetchNextPage,
    hasNextPage,
    isFetchingNextPage: contentIsFetchingNextPage,
  } = useGetGlobalSearchDataLazy({
    segments: ["publishing"],
    searchTerm: searchTermInputValue,
    activeFilters, // Pass activeFilters to the query
    ksConfig: buildListingPageQueryForKS({
      keyword: searchTermInputValue,
      config: {},
      activeSort: {
        kp_date_published: -1,
      },
    }),
    activeSort: {
      kp_date_published: -1,
    },
    enabled: selectedTpls.length > 0,
  });

  const totalCount = contentData?.pages[0]?.count;

  const handleSearch = (id, value) => {
    setSearchTermInputText(value);
    debounce(() => {
      setSearchTermInputValue(value);
    });
  };

  const handleRowClick = (e, data) => {
    const linkToContent = getRoutePathToPublishedContent({
      contentType: data.meta.kp_content_type,
      contentId: data._id,
      SELF_MANAGED_BASE_CONFIGS,
    });
    navigate(linkToContent);
  };

  const listingConfig = {
    wrapper: Wrapper1000,
    onClick: handleRowClick, // Use the extracted handler
    configs: {
      cardLabel: "$thisContentType",
      title: "main.title",
      image: ["main.coverImage"],
      metaBlock: [
        {
          key: "name",
          valuePath: "meta.kp_contributed_by.name",
        },
        {
          key: "date",
          valuePath: "kp_date_published",
        },
      ],
      desc: "desc",
    },
  };

  const autoGenFilters = useAutoGenFilterConfigsFromTpl({
    selectedTpls,
    enableAsyncLoading: true,
    filterScopes: ['doc']
  });

  const hasSearchResults = searchTermInputValue !== '' && contentData?.pages?.[0]?.data?.length > 0;

  const filtersConfig = useMemo(() => {
    if (autoGenFilters?.loading || !hasSearchResults) {
      return [];
    }

    return autoGenFilters.filterConfigs;
  }, [autoGenFilters, hasSearchResults]);

  return (
    <ListingLayout
      // Filter props
      totalCount={totalCount}
      setActiveFilters={setActiveFilters}
      activeFilters={activeFilters}
      presetActiveFilters={[]}
      filterConfig={filtersConfig}
      contentTypes={selectedTpls.map((tpl) => tpl.kp_content_type)}
      // ListingModule props
      listingStyle="ListStyle"
      {...(selectedTpls.length === 0 || searchTermInputValue === ''
         ? {
            contentError: undefined,
            contentStatus: 'success',
            contentData: {pages: [{data: []}]}
         }
         : {
            contentError,
            contentStatus,
            contentData
         }
      )}
      emptyPageConfigSearch={
        selectedTpls.length === 0 ? {
            emptyStatePreset: "globalSearch_noDataSources"
          } : searchTermInputValue && searchTermInputValue !== ""
          ? {
              emptyStatePreset: "searchListing",
              searchTerm: searchTermInputValue,
              onClick: () => {
                setSearchTermInputText("");
                debounce(() => setSearchTermInputValue(""));
              },
            }
          : {
              emptyStatePreset: "globalSearch_initEmptyState"
            }
      }
      sectionTitle=""
      sectionLink=""
      showCount={true}
      listingConfig={listingConfig}
      fetchNextPage={contentFetchNextPage}
      contentIsFetchingNextPage={contentIsFetchingNextPage}
      hasNextPage={hasNextPage}
      ListingWrapper={undefined}
      enableContainerQuery={false}
      inTCI={false}
      builderProps={{}}
      getDataToBeExported={undefined}
      // Actions panel props
      actionRef={undefined}
      isMobile={isMobile}
      actionsPanelStickyTop={0}
      stickyTopToViewportTopGap={0}
      coverConfigExists={false}
      // Sort related props
      showSortDropdown={false}
      activeSort={undefined}
      setActiveSort={undefined}
      sortConfig={undefined}
      // Search related props
      showSearchInput={true}
      searchTermInputText={searchTermInputText}
      handleSearch={handleSearch}
      // Listing style switcher related
      enableListingStyleSwitcher={false}
      setListingStyle={undefined}
    />
  );
};

export default GlobalSearch;
